// src/pages/Dashboard.js
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { Table2, IndianRupee, Clock, AlertTriangle, TrendingUp } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

export default function Dashboard() {
  const [summary, setSummary] = useState(null);
  const [daily,   setDaily]   = useState([]);
  const [tables,  setTables]  = useState([]);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      const [s, d, t] = await Promise.all([
        api.get('/reports?type=summary'),
        api.get('/reports?type=daily&from=' + thirtyDaysAgo() + '&to=' + today()),
        api.get('/tables'),
      ]);
      setSummary(s.data);
      // Guard: daily must be an array
      setDaily(Array.isArray(d.data) ? d.data.slice(0, 14).reverse() : []);
      // Guard: tables must be an array
      setTables(Array.isArray(t.data) ? t.data : []);
    } catch (_) {}
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  const fmt = n => '₹' + Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 });

  return (
    <div>
      <h2 className="page-title">Dashboard</h2>

      {/* Stat cards */}
      <div className="stat-grid">
        <StatCard icon={<Table2/>}        label="Active Tables"   value={summary?.active_tables  ?? '–'} color="blue"/>
        <StatCard icon={<Clock/>}         label="Live Sessions"   value={summary?.active_sessions ?? '–'} color="green"/>
        <StatCard icon={<IndianRupee/>}   label="Today's Revenue" value={fmt(summary?.today_revenue)} color="orange"/>
        <StatCard icon={<AlertTriangle/>} label="Low Stock Items" value={summary?.low_stock_count ?? '–'} color="red"
          onClick={() => navigate('/inventory')}/>
      </div>

      {/* Revenue chart */}
      <div className="card" style={{ marginBottom: 24 }}>
        <div className="card-header">
          <TrendingUp size={18}/> <span>Daily Revenue (Last 14 Days)</span>
        </div>
        {daily.length === 0
          ? <div className="empty-state">No revenue data yet for this period</div>
          : (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={daily} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0"/>
                <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={d => d?.slice(5)}/>
                <YAxis tick={{ fontSize: 11 }} tickFormatter={v => '₹' + (v/1000).toFixed(0) + 'k'}/>
                <Tooltip formatter={v => ['₹' + Number(v).toLocaleString('en-IN'), 'Revenue']} labelFormatter={l => 'Date: ' + l}/>
                <Bar dataKey="total_revenue" fill="#2563eb" radius={[4,4,0,0]}/>
              </BarChart>
            </ResponsiveContainer>
          )
        }
      </div>

      {/* Tables status */}
      <div className="card">
        <div className="card-header"><Table2 size={18}/> <span>Tables Status</span></div>
        {tables.length === 0
          ? <div className="empty-state">No tables yet — go to Tables to add one</div>
          : (
            <div className="table-status-grid">
              {tables.map(t => (
                <div
                  key={t.id}
                  className={`table-status-card status-${t.status}`}
                  onClick={() => t.session_id && navigate(`/session/${t.session_id}`)}
                  style={{ cursor: t.session_id ? 'pointer' : 'default' }}
                >
                  <div className="table-status-name">{t.name}</div>
                  <div className="table-status-badge">{t.status}</div>
                  {/* Show player names from new schema */}
                  {t.player_names && (
                    <div className="table-status-guest">👤 {t.player_names}</div>
                  )}
                  <div className="table-status-rate">₹{t.hourly_rate}/hr</div>
                </div>
              ))}
            </div>
          )
        }
      </div>
    </div>
  );
}

function StatCard({ icon, label, value, color, onClick }) {
  return (
    <div className={`stat-card stat-${color}`} onClick={onClick} style={{ cursor: onClick ? 'pointer' : 'default' }}>
      <div className="stat-icon">{icon}</div>
      <div className="stat-info">
        <div className="stat-value">{value}</div>
        <div className="stat-label">{label}</div>
      </div>
    </div>
  );
}

function today() { return new Date().toISOString().slice(0, 10); }
function thirtyDaysAgo() {
  const d = new Date(); d.setDate(d.getDate() - 30);
  return d.toISOString().slice(0, 10);
}
